import cn from 'classnames';
import { useContext } from 'react';

import { CustomerReportContext } from 'components/grace/Contexts';
import ArrowLeft from 'components/icons/navigation/ArrowLeft';
import CloseX from 'components/icons/navigation/CloseX';
import { TEMPO } from 'context/BrandContext';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useBrand from 'hooks/useBrand';
import { STAGE_TYPES } from 'lib/constants';

const NavigationHeader = ({ onDismiss, helpInterstitial = false }) => {
  const { actionsTaken, currentStage, customerReport, goBack, meal } =
    useContext(CustomerReportContext);
  const isTempo = useBrand(TEMPO);
  const isIngredientSelection = currentStage?.stageType === STAGE_TYPES.ingredientSelection;
  const hasMultipleActions = Array.isArray(actionsTaken) && actionsTaken.length > 1;
  const isResolution = currentStage?.stageType === STAGE_TYPES.resolution;
  const shouldShowNavigation = !isResolution;

  const headerText = () => {
    if (isIngredientSelection) {
      return meal?.displayText;
    }

    return shouldShowNavigation && actionsTaken.at(-1).reportStageOption.name;
  };

  const handleOnClick = useAnalyticsCallback(
    () => onDismiss(customerReport),
    ({ logEvent, events }) => {
      logEvent(events.grace.closeModalClicked);
    },
    []
  );

  const logAndGoBack = useAnalyticsCallback(
    goBack,
    ({ logEvent, events }) => {
      logEvent(events.grace.backClicked, { title: headerText() });
    },
    [actionsTaken]
  );

  const showBackButton = shouldShowNavigation && !helpInterstitial;
  const showHeaderTitle = hasMultipleActions && !helpInterstitial;

  const navigationContent = () => {
    return (
      <span className="flex w-full px-4 py-4">
        {hasMultipleActions && (
          <section className="justify-left mr-auto w-6 shrink-0">
            {showBackButton && (
              <button
                data-testid="grace-goback-button"
                onClick={logAndGoBack}
                aria-label="Back"
                role="button"
              >
                <ArrowLeft className="h-6" />
              </button>
            )}
          </section>
        )}
        {showHeaderTitle && (
          <section data-testid="grace-header-title" className="min-w-0 flex-1 content-center">
            <p className="textMd truncate text-center" role="heading">
              {headerText()}
            </p>
          </section>
        )}
        <section className="justify-right ml-auto w-6 shrink-0">
          <button
            data-testid="grace-close-button"
            onClick={handleOnClick}
            aria-label="Close"
            role="closeButton"
          >
            <CloseX className="h-6" />
          </button>
        </section>
      </span>
    );
  };

  const backgroundClass =
    isResolution && !helpInterstitial && (isTempo ? 'bg-tempo-gray-40' : 'bg-cream-100');

  return (
    <>
      <section className={cn(backgroundClass)} role="navigation">
        {navigationContent()}
      </section>
    </>
  );
};

NavigationHeader.propTypes = {
  onDismiss: PropTypes.func,
  helpInterstitial: PropTypes.bool,
};

export default NavigationHeader;
